import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { isNil } from 'lodash';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { OrganizationUnitAiConfigService } from 'src/api/customer/organization-unit-ai-config/organization-unit-ai-config.service';
import { I18nToastProvider } from 'src/providers/i18n-toast.provider';
import { IAiConfig, IAiUsageQuotaUsage } from 'typings/doenkids/doenkids';

/**
 * The AI configuration of a customer organization unit: whether AI is on, the
 * default monthly token budget, and the quota windows spent against it.
 *
 * DoenKids administrators only. Every endpoint behind this form makes the same
 * check, so an organization admin must never be shown it.
 */
@Component({
  selector: 'app-organization-unit-ai-config-form',
  templateUrl: './organization-unit-ai-config-form.component.html',
  styleUrls: ['./organization-unit-ai-config-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class OrganizationUnitAiConfigFormComponent implements OnChanges {
  /** The customer organization unit to configure */
  @Input() organizationUnitId: number;

  public config: IAiConfig | null = null;

  public usageQuotas: IAiUsageQuotaUsage[] = [];

  public loading = false;

  public saving = false;

  /** Guards against an older fetch landing after a newer one */
  private fetchGeneration = 0;

  /** Bound to `!archived`: an archived config is how AI is switched off */
  public aiEnabled = false;

  public tokenLimitControl = new FormControl<number | null>(null, [
    Validators.required,
    Validators.min(1),
    // The API parseInts the budget rather than refusing a fraction, so 1.5
    // would be accepted and silently stored as 1.
    //
    (control) => (isNil(control.value) || Number.isInteger(Number(control.value)) ? null : { integer: true }),
  ]);

  constructor(
    private organizationUnitAiConfigService: OrganizationUnitAiConfigService,
    private $i18nToastProvider: I18nToastProvider,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (!isNil(changes.organizationUnitId) && !isNil(this.organizationUnitId)) {
      this.fetch();
    }
  }

  /**
   * True once a config row exists, which is what freezes the window length.
   *
   * An archived config still counts: it is how AI is switched off, and the row
   * is revived rather than recreated, so `quota_period` must not be sent again.
   */
  public get isConfigured(): boolean {
    return !isNil(this.config);
  }


  public get canSave(): boolean {
    return !this.saving && this.tokenLimitControl.valid;
  }

  public async fetch(): Promise<void> {
    const generation = ++this.fetchGeneration;
    const organizationUnitId = this.organizationUnitId;

    this.loading = true;

    // The organisation can change under a LIVE component: the details page
    // reassigns its id rather than being recreated. Without emptying here, a
    // failed fetch leaves the previous organisation's budget on screen and
    // editable, and Save would write it to the new one.
    //
    this.applyConfig(null);
    this.usageQuotas = [];
    this.cdr.markForCheck();

    try {
      const [config, usageQuotas] = await Promise.all([
        this.organizationUnitAiConfigService.fetch(organizationUnitId),
        // A 403 here only means AI is not enabled for this organisation, which
        // is the normal state for one that has never been configured. Failing
        // the whole load for it put a red toast on every such organisation.
        //
        this.organizationUnitAiConfigService.fetchUsageQuotas(organizationUnitId).catch(() => ({ items: [], total: 0 })),
      ]);

      if (generation !== this.fetchGeneration) {
        return;
      }

      this.applyConfig(config);
      this.usageQuotas = usageQuotas.items;
    } catch (error) {
      if (generation !== this.fetchGeneration) {
        return;
      }

      this.$i18nToastProvider.error(_('organization_unit.ai_config.fetch_failed'));
    }

    if (generation !== this.fetchGeneration) {
      return;
    }

    this.loading = false;
    this.cdr.markForCheck();
  }

  public toggleAiEnabled(checked: boolean): void {
    this.aiEnabled = checked;
  }

  /**
   * Creates or updates the configuration, and carries the budget over to the
   * window already running.
   *
   * Each window copied its limit from the config when it was created, so the
   * config alone only reaches future windows. Editing the running window
   * separately, in the table, meant two inputs that both looked like "this
   * month's budget"; this field is now the only one.
   *
   * `quota_period` is sent only on creation: the API rejects a changed value
   * outright, and re-sending the stored one achieves nothing.
   */
  public async save(): Promise<void> {
    if (!this.canSave) {
      return;
    }

    this.saving = true;
    this.cdr.markForCheck();

    const organizationUnitId = this.organizationUnitId;
    const tokenLimit = Number(this.tokenLimitControl.value);
    const activeQuota = this.usageQuotas.find((quota) => quota.is_active);
    let stored = false;

    try {
      const saved = await this.organizationUnitAiConfigService.update(organizationUnitId, {
        token_limit: tokenLimit,
        archived: !this.aiEnabled,
        ...(this.isConfigured ? {} : { quota_period: 'monthly' as const }),
      });

      this.applyConfig(saved);
      stored = true;
      this.$i18nToastProvider.success(_('organization_unit.ai_config.save_success'));
    } catch (error) {
      // The API refuses a config on a unit that is not its own customer, which
      // is the mistake most worth naming.
      //
      const message = (error as { error?: { message?: string } })?.error?.message;

      this.$i18nToastProvider.error(
        isNil(message) ? _('organization_unit.ai_config.save_failed') : _('organization_unit.ai_config.save_failed_reason'),
        { reason: message },
      );
    }

    // Outside the write's try: switching AI off archives the config, which
    // makes this very call answer 403. Reporting that as a failed save told
    // the administrator the configuration had not been stored when it had —
    // on the toggle's single most common use.
    //
    if (stored) {
      // Skipped when AI was just switched off: the window answers 403 then,
      // and its limit no longer matters until AI is back on.
      //
      if (this.aiEnabled && !isNil(activeQuota) && activeQuota.token_limit !== tokenLimit) {
        try {
          await this.organizationUnitAiConfigService.updateUsageQuota(organizationUnitId, activeQuota.id, tokenLimit);
        } catch (error) {
          this.$i18nToastProvider.error(_('organization_unit.ai_config.active_window_failed'));
        }
      }

      try {
        this.usageQuotas = (await this.organizationUnitAiConfigService.fetchUsageQuotas(organizationUnitId)).items;
      } catch (error) {
        // A config write does not change the stored windows, so the list on
        // screen stays as good an answer as it was a moment ago.
        //
      }
    }

    this.saving = false;
    this.cdr.markForCheck();
  }

  /** The share of a window that has been spent, for the admin-facing table. */
  public usedPercentage(quota: IAiUsageQuotaUsage): number {
    if (isNil(quota.token_limit) || quota.token_limit <= 0) {
      return 100;
    }

    return Math.min(100, Math.ceil(((quota.used_tokens ?? 0) / quota.token_limit) * 100));
  }

  public trackByQuotaId(_index: number, quota: IAiUsageQuotaUsage): number {
    return quota.id;
  }

  /**
   * Reflects a config onto the form.
   *
   * A fetch reports an archived config as null, because the API reads through
   * its archived filter, so an organisation with AI switched off starts with an
   * empty form. A save returns the row itself, archived or not, and that row is
   * kept: the stored budget stays visible and the window length stays frozen.
   */
  private applyConfig(config: IAiConfig | null): void {
    this.config = config;
    this.aiEnabled = !isNil(config) && config.archived !== true;
    this.tokenLimitControl.setValue(config?.token_limit ?? null, { emitEvent: false });
    this.tokenLimitControl.markAsPristine();
  }
}
